import { PrismicRichText } from '@prismicio/react';
import { Checkbox } from '@remote-com/mushu/src/components/Ui/Form/Checkbox';
import { InputController } from '@remote-com/mushu/src/components/Ui/Form/Input';
import { CheckboxState } from '@remote-com/mushu/src/domains/registration/onboarding/shared/styles/Form';
import Image from 'next/image';
import { useRef, useContext } from 'react';
import HeaderContext from 'remote-com-shared/components/Header/context';
import { Hero, ImageBackground } from 'remote-com-shared/components/Hero';
import HeroContext from 'remote-com-shared/components/Hero/context';
import {
  Title,
  Description,
  ContentMain,
  ContentLayout,
  Main,
} from 'remote-com-shared/components/Hero/styles';
import { FormContainer } from 'remote-com-shared/components/ModalForm/styles';
import { TwoStepForm, TwoStepFormModalButton } from 'remote-com-shared/components/TwoStepForm';
import { StyledButton, SubmitButton } from 'remote-com-shared/components/TwoStepForm/styles';
import { reasonOptions } from 'remote-com-shared/data';
import { whenProp } from 'remote-com-shared/style/helpers';
import styled, { css } from 'styled-components';

import { CustomCTAHeader } from '@/src/components/Header';
import { transformProperties } from '@/src/domains/prismic/helpers';
import { fromDesktopBreakpoint, desktopBreakpoint } from '@/src/domains/shared/breakpoints';
import { HeaderCTAButton } from '@/src/views/GlobalWorkforceReportPage/slices/HeroSlice';

const HeroBackground = styled(ImageBackground)`
  && {
    && {
      right: 0;
      bottom: -200px;
      display: flex;
      max-width: 55%;
      z-index: 1;

      ${desktopBreakpoint(css`
        display: none;
      `)}
    }
  }
`;

const WhiteHeroTone = css`
  background-color: #000000;
  background-image: none;

  ${StyledButton}, ${SubmitButton} {
    background-color: #e03d5c;
    color: var(--colors-blank);
    box-shadow: none;
  }
`;

const HeroWrapper = styled(Hero)`
  && {
    font-family: var(--typography-fontBrand);
    ${fromDesktopBreakpoint(css`
      ${ContentMain} {
        max-width: 50%;
        padding-top: 100px;
      }

      ${ContentLayout} {
        gap: 25px;
      }
    `)}

    ${Title} {
      font-family: var(--typography-fontBossa);
    }

    ${Description} {
      color: var(--colors-blank);
    }

    ${Main} {
      z-index: 2;
    }

    ${FormContainer} {
      max-width: 450px;

      ${desktopBreakpoint(css`
        margin-top: 50px;
      `)}
    }

    ${({ $tone }) => {
      switch ($tone) {
        case 'orange':
          return css`
            ${WhiteHeroTone};

            ${StyledButton}, ${SubmitButton} {
              background-color: #ff817b;
            }
            background-color: var(--colors-darkBlue);
          `;
        case 'red':
          return css`
            ${WhiteHeroTone};
          `;

        case 'green':
          return css`
            ${WhiteHeroTone};

            ${StyledButton}, ${SubmitButton} {
              background-color: #00b568;
            }
          `;
        default:
          return css`
            background-color: #5700ff;
            background-image: none;
          `;
      }
    }}

    // to override default borderr-color with !important
    #location > div,
    #reason > div,
    #target_countries > div {
      &:focus-visible,
      &:hover,
      &:focus-within {
        border-color: var(--colors-darkBlue) !important;
      }
    }

    ${InputController} {
      input {
        &:focus-visible,
        &:hover,
        &:focus-within {
          --inputBorderColor: var(--colors-darkBlue);
        }
      }

      ${CheckboxState} {
        border-color: #778ca3;
      }
      ${Checkbox} {
        a {
          color: var(--colors-darkBlue);
          text-decoration: underline;
          text-decoration-color: var(--colors-irisBlue);
          text-underline-offset: 3px;
        }
      }
      span {
        color: var(--colors-darkBlue);
      }
    }
  }
`;

export const HeaderButton = styled(HeaderCTAButton)`
  && {
    ${({ theme }) => theme.typography.body};
    text-transform: uppercase;
    color: var(--colors-blank);

    ${({ $tone }) => {
      switch ($tone) {
        case 'red':
          return css`
            background-color: #e03d5c;
          `;
        case 'green':
          return css`
            background-color: #00b568;
          `;
        case 'orange':
          return css`
            color: var(--colors-darkBlue);

            ${whenProp(
              '$isFixed',
              css`
                color: var(--colors-blank);
              `
            )}
          `;
        case 'purple':
        default:
          return css`
            background-color: var(--colors-blank);
            color: var(--colors-darkBlue);

            ${whenProp(
              '$isFixed',
              css`
                background-color: #5700ff;
                color: var(--colors-blank);
              `
            )}
          `;
      }
    }}
  }
`;

export function HeroSlice({ slice }) {
  const { primary } = transformProperties(slice);
  const { context } = slice;
  const { title, description, bgImage, headerLink, headerLabel, awardImage } = primary;

  const { tone } = context;

  const headerRef = useRef(null);
  const { isIntersecting } = useContext(HeroContext);

  const { hubspotFormDefinition, formTitle, formDescription, formButtonLabel } = context;
  const scheduleDemoReasonFields = reasonOptions.slice(0, 2);

  return (
    <>
      <HeaderContext.Provider value={headerRef}>
        <CustomCTAHeader
          cta={
            headerLink ? (
              <HeaderButton
                size="lg"
                href={headerLink?.url}
                target={headerLink?.target}
                rel="nofollow"
                $isFixed={!isIntersecting}
                $tone={tone}
              >
                {headerLabel || 'Learn more'}
              </HeaderButton>
            ) : (
              <TwoStepFormModalButton
                button={{ as: HeaderButton, $tone: tone, $isFixed: !isIntersecting }}
              >
                {headerLabel || 'Schedule a demo'}
              </TwoStepFormModalButton>
            )
          }
        />
        <HeroWrapper
          $tone={tone}
          title={<PrismicRichText field={title} />}
          description={<PrismicRichText field={description} />}
          aside={
            <TwoStepForm
              formTitle={formTitle || 'Schedule Demo'}
              formDescription={formDescription}
              buttonLabel={formButtonLabel || 'Schedule Demo'}
              hubspotFormDefinition={hubspotFormDefinition}
              formID="hero-two-step-form"
              scheduleDemoReasonFields={scheduleDemoReasonFields}
              companyEmailAddressesOnly
            />
          }
          background={
            <HeroBackground as="div">
              {bgImage && (
                <Image
                  src={bgImage?.url}
                  width={bgImage?.dimensions.width}
                  height={bgImage?.dimensions.height}
                  quality="100"
                  alt={bgImage?.alt || ''}
                />
              )}
            </HeroBackground>
          }
          actions={
            awardImage && (
              <Image
                src={awardImage?.url}
                width={awardImage?.dimensions.width}
                height={awardImage?.dimensions.height}
                quality="100"
                alt={awardImage?.alt || ''}
              />
            )
          }
        />
      </HeaderContext.Provider>
    </>
  );
}
